/**
 * The beam that ties the dial to the cards (KEY-532).
 *
 * With the selection turned to East, a wedge of the selection's colour spans from its outer edge to the card
 * stack, so the cards read as PROJECTED from the thing you picked rather than as a panel that happens to sit
 * alongside. Four points: two on the dial at the selection's half-angle, two down the card stack's left edge.
 *
 * Pure, because the failure is silent. Measured from the wrong box, or with the wrong half-angle, the beam
 * still draws — just skewed, or spanning a section when a single action point is selected — and nothing
 * throws to tell you.
 */

/** Where the dial and the card stack actually are, in the coordinates of the row that contains both. */
export interface BeamGeometry {
  /** Dial centre. */
  cx: number;
  cy: number;
  /** The dial box's width — the radii below are fractions of it. */
  dialWidth: number;
  /** The card stack's left edge, and its top and bottom. */
  cardLeft: number;
  cardTop: number;
  cardBottom: number;
}

export type BeamLevel = "none" | "section" | "segment";

/**
 * The radius the beam leaves the dial at, as a fraction of the dial box's width.
 *
 * A segment beam starts at the segment ring's outer edge; a section beam at the outer edge of the section
 * band, which is where a section's own status edge is drawn. Neutral splits the difference and leaves from
 * the rim. These mirror DialView's geometry constants over its 100-unit viewBox — AR_OUT 38.8 and SL_OUT
 * 49.4 — so a change there has to change here too.
 */
export function beamRadius(level: BeamLevel): number {
  if (level === "segment") return 0.388;
  if (level === "section") return 0.494;
  return 0.5;
}

/**
 * Half the angular width of the selection, in radians — how wide the beam is where it leaves the dial.
 *
 * Nothing selected spreads the full quarter (π/2): the neutral beam is a wash tying the overview cards to
 * the dial as a whole, not a pointer at any part of it.
 */
export function beamHalfAngle(level: BeamLevel, sectionCount: number, segmentCount: number): number {
  if (level === "none" || sectionCount <= 0) return Math.PI / 2;
  const perSection = (2 * Math.PI) / sectionCount;
  if (level === "section") return perSection / 2;
  return segmentCount > 0 ? perSection / segmentCount / 2 : perSection / 2;
}

/**
 * The beam as an SVG polygon `points` string, or null when the geometry has not been measured yet.
 *
 * The two dial-side points are mirrored about the horizontal because the selection is at East: the beam
 * opens symmetrically above and below the 3 o'clock line.
 */
export function beamPoints(geometry: BeamGeometry | null, level: BeamLevel, halfAngle: number): string | null {
  if (!geometry) return null;
  const { cx, cy, dialWidth, cardLeft, cardTop, cardBottom } = geometry;
  if (!(dialWidth > 0) || !(cardBottom > cardTop)) {
    // A collapsed card stack (or a dial box that has not laid out) would draw a degenerate sliver.
    return null;
  }

  const r = dialWidth * beamRadius(level);
  const x = cx + r * Math.cos(halfAngle);
  const yUp = cy - r * Math.sin(halfAngle);
  const yDown = cy + r * Math.sin(halfAngle);

  // The card edge must be to the RIGHT of where the beam leaves the dial, or the polygon folds back on
  // itself and paints over the dial. Happens at narrow widths before the stacked layout takes over.
  if (cardLeft <= x) return null;

  return `${x},${yUp} ${cardLeft},${cardTop} ${cardLeft},${cardBottom} ${x},${yDown}`;
}

/** How solid the beam is where it leaves the dial. A neutral wash is deliberately fainter than a pointer. */
export function beamOpacity(level: BeamLevel): number {
  return level === "none" ? 0.28 : 0.55;
}
